import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { submitPrescription, uploadReimbursementFile } from '../../../Services/APIs/reimbursement';
import { FileField, TextField, SelectField, ReviewField, ReviewFile } from './formFields';
import { acceptFor } from './reimbursementStatus';
import { getPhonepeMembers } from '../phonepeSession';

const FILE_FIELDS = [
    'prescription_file',
    'upper_occlusal_photo',
    'lower_occlusal_photo',
    'problem_area_photo',
    'additional_photo',
    'pre_op_xray_1',
    'pre_op_xray_2',
];

const emptyFields = () => ({
    clinic_name: '',
    clinic_address: '',
    clinic_doctor: '',
    clinic_contact: '',
    clinical_notes: '',
});

// Starts a Reimbursement: the Reimbursement Prescription (the dentist's letterhead prescription
// plus clinical photos) is reviewed before the member gets treatment. Native 2-page replica of the
// dental360.toothlens.com/prescription form (ADR-0002). Each file uploads to S3 as soon as it's
// picked (see uploadReimbursementFile) — the form only ever submits the resulting URLs.
// Re-uploads a reviewer asks for later go through ReuploadForm, not this form.
// The patient is picked from the people on the member's policy and sent as their wallet uid.
const PrescriptionForm = ({ onClose, onSuccess }) => {
    const [step, setStep] = useState(1);
    const [members] = useState(getPhonepeMembers);
    // With only one person on the policy there is nothing to choose.
    const [patientUid, setPatientUid] = useState(() => (members.length === 1 ? members[0].uid : ''));
    const [fields, setFields] = useState(emptyFields);
    const [uploads, setUploads] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isDone, setIsDone] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    const setField = (name, value) => setFields((prev) => ({ ...prev, [name]: value }));

    const setFile = async (name, file) => {
        if (!file) {
            setUploads((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
            return;
        }
        setUploads((prev) => ({ ...prev, [name]: { file, status: 'uploading', url: null, error: null } }));
        try {
            const response = await uploadReimbursementFile(file, { type: 'prescription', field: name });
            setUploads((prev) => ({ ...prev, [name]: { file, status: 'done', url: response.data?.data?.url, error: null } }));
        } catch (err) {
            const message = err?.response?.data?.message || 'Upload failed. Please try again.';
            setUploads((prev) => ({ ...prev, [name]: { file, status: 'error', url: null, error: message } }));
        }
    };

    const isAnyUploading = FILE_FIELDS.some((name) => uploads[name]?.status === 'uploading');

    const fileField = (name, label, required = false) => (
        <FileField
            label={label}
            required={required}
            accept={acceptFor(name)}
            testId={`upload-${name}`}
            file={uploads[name]?.file}
            uploading={uploads[name]?.status === 'uploading'}
            error={uploads[name]?.error}
            onChange={(f) => setFile(name, f)}
        />
    );

    const patient = members.find((member) => member.uid === patientUid);
    const patientLabel = (member) => (member.relationship ? `${member.name} (${member.relationship})` : member.name);

    const goToReview = (e) => {
        e.preventDefault();
        if (!members.length) {
            setError('We could not load the people on your policy. Please log out and log in again.');
            return;
        }
        if (!patient) {
            setError('Please select the patient.');
            return;
        }
        if (!fields.clinic_name.trim() || !fields.clinic_address.trim() || !fields.clinic_doctor.trim() || !fields.clinic_contact.trim()) {
            setError('Clinic name, address, doctor, and contact are required.');
            return;
        }
        if (!fields.clinical_notes.trim()) {
            setError('Clinical notes are required.');
            return;
        }
        if (isAnyUploading) {
            setError('Please wait for uploads to finish.');
            return;
        }
        if (!uploads.prescription_file?.url) {
            setError('Please upload the prescription.');
            return;
        }
        if (!uploads.upper_occlusal_photo?.url || !uploads.lower_occlusal_photo?.url || !uploads.problem_area_photo?.url) {
            setError('Please upload the Upper Occlusal, Lower Occlusal, and Problem Area photos.');
            return;
        }
        setError('');
        setStep(2);
    };

    const handleSubmit = async () => {
        if (!confirmed) {
            setError('Please confirm the information is accurate.');
            return;
        }
        setIsSubmitting(true);
        setError('');
        try {
            const payload = { ...fields, uid: patient.uid };
            payload.reimbursement_approval_file_url = uploads.prescription_file.url;
            payload.upper_occlusal_photo_url = uploads.upper_occlusal_photo.url;
            payload.lower_occlusal_photo_url = uploads.lower_occlusal_photo.url;
            payload.problem_area_photo_url = uploads.problem_area_photo.url;
            if (uploads.additional_photo?.url) payload.additional_photo_url = uploads.additional_photo.url;
            const preOpXrayUrls = [uploads.pre_op_xray_1?.url, uploads.pre_op_xray_2?.url].filter(Boolean);
            if (preOpXrayUrls.length) payload.pre_op_xray_urls = preOpXrayUrls;

            await submitPrescription(payload);
            setIsDone(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to submit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isDone) {
        return (
            <div className="p-8 flex flex-col items-center text-center" data-testid="prescription-submitted">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-gray-800 font-bold text-lg mb-1">Reimbursement requested</h3>
                <p className="text-gray-400 text-sm mb-6">We'll review your reimbursement prescription and update its status here.</p>
                <button
                    onClick={onSuccess}
                    className="w-full py-3 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors"
                >
                    Done
                </button>
            </div>
        );
    }

    return (
        <>
            {/* Header */}
            <div className="bg-purple-600 p-5 rounded-t-2xl flex justify-between items-center flex-shrink-0">
                <div>
                    <h2 className="text-lg font-bold text-white">Request Reimbursement</h2>
                    <p className="text-purple-100 text-xs mt-1">Reimbursement prescription · Step {step} of 2</p>
                </div>
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                >
                    <X className="w-4 h-4 text-white" />
                </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
                {error && (
                    <p role="alert" className="text-red-600 text-sm bg-red-50 border border-red-100 rounded-lg p-3 mb-4">{error}</p>
                )}

                {step === 1 && (
                    <form onSubmit={goToReview} className="space-y-4">
                        <p className="text-xs text-gray-500">
                            Consulted a dentist outside the ToothLens network? Share their prescription for approval before your treatment.
                        </p>
                        <SelectField
                            label="Patient"
                            required
                            placeholder="Select the patient"
                            data-testid="prescription-patient"
                            value={patientUid}
                            onChange={(e) => setPatientUid(e.target.value)}
                            options={members.map((member) => ({ value: member.uid, label: patientLabel(member) }))}
                        />
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide pt-2">Clinic details</p>
                        <TextField label="Clinic name" required value={fields.clinic_name} onChange={(e) => setField('clinic_name', e.target.value)} />
                        <TextField label="Clinic address" required value={fields.clinic_address} onChange={(e) => setField('clinic_address', e.target.value)} />
                        <TextField label="Doctor" required value={fields.clinic_doctor} onChange={(e) => setField('clinic_doctor', e.target.value)} />
                        <TextField label="Clinic contact" required value={fields.clinic_contact} onChange={(e) => setField('clinic_contact', e.target.value)} />

                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide pt-2">Prescription &amp; photos</p>
                        {fileField('prescription_file', 'Prescription', true)}
                        <TextField label="Clinical notes" required value={fields.clinical_notes} onChange={(e) => setField('clinical_notes', e.target.value)} />
                        {fileField('upper_occlusal_photo', 'Upper Occlusal photo', true)}
                        {fileField('lower_occlusal_photo', 'Lower Occlusal photo', true)}
                        {fileField('problem_area_photo', 'Problem Area photo', true)}
                        {fileField('additional_photo', 'Additional photo (optional)')}
                        {fileField('pre_op_xray_1', 'Pre-Op X-ray 1 (optional)')}
                        {fileField('pre_op_xray_2', 'Pre-Op X-ray 2 (optional)')}

                        <button
                            type="submit"
                            disabled={isAnyUploading}
                            className="w-full py-3 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isAnyUploading ? 'Uploading…' : <>Review &amp; Continue <ArrowRight className="w-4 h-4" /></>}
                        </button>
                    </form>
                )}

                {step === 2 && (
                    <div className="space-y-4">
                        <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                            <div className="text-sm">
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Patient</p>
                                <ReviewField label="Patient" value={patient ? patientLabel(patient) : ''} />
                            </div>

                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Clinic details</p>
                                <div className="space-y-1.5 text-sm">
                                    <ReviewField label="Clinic name" value={fields.clinic_name} />
                                    <ReviewField label="Clinic address" value={fields.clinic_address} />
                                    <ReviewField label="Doctor" value={fields.clinic_doctor} />
                                    <ReviewField label="Clinic contact" value={fields.clinic_contact} />
                                </div>
                            </div>

                            <div className="text-sm">
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Clinical notes</p>
                                <ReviewField label="Notes" value={fields.clinical_notes} />
                            </div>

                            <div className="space-y-3">
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Uploaded files</p>
                                <ReviewFile label="Prescription" file={uploads.prescription_file?.file} />
                                <ReviewFile label="Upper Occlusal photo" file={uploads.upper_occlusal_photo?.file} />
                                <ReviewFile label="Lower Occlusal photo" file={uploads.lower_occlusal_photo?.file} />
                                <ReviewFile label="Problem Area photo" file={uploads.problem_area_photo?.file} />
                                <ReviewFile label="Additional photo" file={uploads.additional_photo?.file} />
                                <ReviewFile label="Pre-Op X-ray 1" file={uploads.pre_op_xray_1?.file} />
                                <ReviewFile label="Pre-Op X-ray 2" file={uploads.pre_op_xray_2?.file} />
                            </div>
                        </div>

                        <label className="flex items-start gap-2 text-xs text-gray-600">
                            <input type="checkbox" className="mt-0.5" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="prescription-confirm" />
                            I confirm the information and documents provided are accurate to the best of my knowledge.
                        </label>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setStep(1)}
                                disabled={isSubmitting}
                                className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                data-testid="prescription-submit"
                                className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors disabled:opacity-50"
                            >
                                {isSubmitting ? 'Submitting…' : 'Request reimbursement'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default PrescriptionForm;
